import { Box, ButtonBase, Chip, CircularProgress, Typography } from '@mui/material';
import type { Starter } from './starters';

const TONE_COLORS = { warning: 'warning', error: 'error', info: 'info', neutral: 'default' } as const;

/** "Good morning, Ada": the part of the day `hour` falls in, and the first word of the user's name. */
export function greeting(userName: string, hour: number): string {
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const first = userName.trim().split(/\s+/)[0];
  return first ? `Good ${part}, ${first}` : `Good ${part}`;
}

interface StarterCardsProps {
  userName: string;
  starters: Starter[];
  /** The starters' data is still loading; a spinner shows in their place. */
  loading: boolean;
  disabled: boolean;
  onPick: (starter: Starter) => void;
}

/** An empty conversation: a greeting and what the Chief of Staff can pick up right now. */
export function StarterCards({ userName, starters, loading, disabled, onPick }: StarterCardsProps) {
  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', mt: 4 }}>
      <Typography variant="h6">{greeting(userName, new Date().getHours())}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ask your Chief of Staff anything, start with one of these, or type / for commands.
      </Typography>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} aria-label="Loading suggestions" />
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5 }}>
        {!loading &&
          starters.map((starter) => (
            <ButtonBase
              key={starter.key}
              disabled={disabled}
              onClick={() => onPick(starter)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: 1,
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                textAlign: 'left',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {starter.tag && <Chip label={starter.tag} size="small" color={TONE_COLORS[starter.tone]} variant="outlined" />}
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {starter.text}
              </Typography>
            </ButtonBase>
          ))}
      </Box>
    </Box>
  );
}
